<template>
  <div class="profile-container">
    <TopHeader />

    <div class="main-wrapper">
      <div class="profile-layout">
        <!-- 左侧：个人信息 -->
        <div class="profile-card">
          <h2 class="card-title">个人信息</h2>
          
          <!-- 头像区域 -->
          <div class="avatar-section">
            <div class="avatar-wrapper" @click="triggerUpload">
              <img :src="userAvatar" class="profile-avatar" />
              <div class="avatar-overlay">
                <span>更换头像</span>
              </div>
              <input 
                ref="avatarInput" 
                type="file" 
                accept="image/*" 
                style="display: none" 
                @change="handleAvatarChange"
              />
            </div>
            <div class="avatar-info">
              <span class="user-name">{{ userInfo.userName || '用户' }}</span>
              <span class="user-grade">{{ userInfo.gradeName || '' }}</span>
            </div>
          </div>

          <!-- 信息表单 -->
          <div class="info-form">
            <div class="form-item">
              <label class="form-label">学号</label>
              <div class="form-value disabled">{{ userInfo.studentNo }}</div>
            </div>
            
            <div class="form-item">
              <label class="form-label">邮箱</label>
              <div class="form-input-wrapper">
                <input 
                  v-if="editingEmail" 
                  v-model="newEmail" 
                  type="email" 
                  class="form-input"
                  placeholder="请输入新邮箱"
                />
                <div v-else class="form-value">{{ userInfo.email }}</div>
                <button v-if="!editingEmail" class="edit-btn" @click="startEditEmail">修改</button>
                <template v-else>
                  <button class="save-btn" @click="saveEmail">保存</button>
                  <button class="cancel-btn" @click="cancelEditEmail">取消</button>
                </template>
              </div>
            </div>

            <div class="form-item">
              <label class="form-label">姓名</label>
              <div class="form-value">{{ userInfo.userName }}</div>
            </div>

            <div class="form-item">
              <label class="form-label">年级</label>
              <div class="form-value">{{ userInfo.gradeName || '未设置' }}</div>
            </div>
          </div>
        </div>

        <!-- 右侧：修改密码 -->
        <div class="password-card">
          <h2 class="card-title">修改密码</h2>
          <div class="password-form">
            <div class="form-item">
              <label class="form-label">原密码</label>
              <input v-model="passwordForm.oldPassword" type="password" class="form-input" placeholder="请输入原密码" />
            </div>
            <div class="form-item">
              <label class="form-label">新密码</label>
              <input v-model="passwordForm.newPassword" type="password" class="form-input" placeholder="请输入新密码" />
            </div>
            <div class="form-item">
              <label class="form-label">确认密码</label>
              <input v-model="passwordForm.confirmPassword" type="password" class="form-input" placeholder="请再次输入新密码" />
            </div>
            <button class="submit-btn" @click="changePassword">确认修改</button>
          </div>
        </div>
      </div>
    </div>

    <Toast ref="toast" />
  </div>
</template>

<script>
import TopHeader from '@/components/TopHeader.vue'
import Toast from '@/components/Toast.vue'
import { updateEmail, updatePassword, updateAvatar } from '@/api/profile'
import config from '@/utils/config'

export default {
  name: 'ProfilePage',
  components: {
    TopHeader,
    Toast
  },
  data() {
    return {
      userInfo: {},
      editingEmail: false,
      newEmail: '',
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
    }
  },
  computed: {
    userAvatar() {
      if (this.userInfo && this.userInfo.avatar) {
        if (this.userInfo.avatar.startsWith('http')) {
          return this.userInfo.avatar
        }
        return config.imageBaseURL + this.userInfo.avatar
      }
      return require('@/assets/image/touxiang.png')
    }
  },
  created() {
    const stored = localStorage.getItem('userInfo')
    if (stored) {
      this.userInfo = JSON.parse(stored)
    }
  },
  methods: {
    startEditEmail() {
      this.newEmail = this.userInfo.email
      this.editingEmail = true
    },
    cancelEditEmail() {
      this.editingEmail = false
      this.newEmail = ''
    },
    async saveEmail() {
      if (!this.newEmail) {
        this.$refs.toast.show('请输入邮箱')
        return
      }
      const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailReg.test(this.newEmail)) {
        this.$refs.toast.show('请输入正确的邮箱格式')
        return
      }
      try {
        await updateEmail({ email: this.newEmail })
        this.userInfo.email = this.newEmail
        localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
        this.editingEmail = false
        this.$refs.toast.show('邮箱修改成功')
      } catch (error) {
        this.$refs.toast.show(error.message || '修改失败')
      }
    },
    async changePassword() {
      const { oldPassword, newPassword, confirmPassword } = this.passwordForm
      if (!oldPassword) {
        this.$refs.toast.show('请输入原密码')
        return
      }
      if (!newPassword) {
        this.$refs.toast.show('请输入新密码')
        return
      }
      if (newPassword.length < 6) {
        this.$refs.toast.show('新密码长度不能少于6位')
        return
      }
      if (newPassword !== confirmPassword) {
        this.$refs.toast.show('两次输入的密码不一致')
        return
      }
      try {
        await updatePassword({ oldPassword, newPassword })
        this.$refs.toast.show('密码修改成功')
        this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      } catch (error) {
        this.$refs.toast.show(error.message || '修改失败')
      }
    },
    triggerUpload() {
      this.$refs.avatarInput.click()
    },
    async handleAvatarChange(e) {
      const file = e.target.files[0]
      if (!file) return
      
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        this.$refs.toast.show('请选择图片文件')
        return
      }
      
      // 验证文件大小（最大2MB）
      if (file.size > 2 * 1024 * 1024) {
        this.$refs.toast.show('图片大小不能超过2MB')
        return
      }
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        const res = await updateAvatar(formData)
        // 更新头像 - 返回格式是 {code: 200, msg: "xxx", data: "/profile/avatar/xxx.png"}
        const avatarPath = res.data || res.msg || res
        this.userInfo.avatar = avatarPath
        localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
        this.$refs.toast.show('头像更新成功')
      } catch (error) {
        this.$refs.toast.show(error.message || '上传失败')
      }
      
      // 清空input
      e.target.value = ''
    }
  }
}
</script>

<style scoped>
.profile-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 40px;
  padding-top: 70px;
}

.main-wrapper {
  max-width: 1100px;
  margin: 30px auto;
  padding: 0 20px;
}

.profile-layout {
  display: flex;
  gap: 20px;
}

.profile-card {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  padding: 30px;
}

.password-card {
  width: 400px;
  background: #fff;
  border-radius: 8px;
  padding: 30px;
}

.card-title {
  font-size: 18px;
  color: #333;
  font-weight: 500;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.avatar-section {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
}

.avatar-wrapper {
  position: relative;
  cursor: pointer;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.avatar-overlay span {
  color: #fff;
  font-size: 12px;
}

.avatar-wrapper:hover .avatar-overlay {
  opacity: 1;
}

.avatar-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.user-name {
  font-size: 18px;
  color: #333;
  font-weight: 500;
}

.user-grade {
  font-size: 14px;
  color: #666;
}

.info-form {
  margin-bottom: 0;
}

.form-item {
  display: flex;
  align-items: center;
  padding: 18px 0;
  border-bottom: 1px solid #f0f0f0;
}

.form-item:last-child {
  border-bottom: none;
}

.form-label {
  width: 80px;
  font-size: 14px;
  color: #666;
  flex-shrink: 0;
}

.form-value {
  flex: 1;
  font-size: 14px;
  color: #333;
}

.form-value.disabled {
  color: #999;
}

.form-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.form-input:focus {
  border-color: #3d8c40;
}

.edit-btn {
  padding: 6px 16px;
  background: #fff;
  color: #3d8c40;
  border: 1px solid #3d8c40;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-btn:hover {
  background: #f0f7f0;
}

.save-btn {
  padding: 6px 16px;
  background: #3d8c40;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.save-btn:hover {
  background: #2e7d32;
}

.cancel-btn {
  padding: 6px 16px;
  background: #fff;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.cancel-btn:hover {
  border-color: #ccc;
}

.password-form .form-item {
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  border-bottom: none;
  padding: 12px 0;
}

.password-form .form-label {
  width: auto;
}

.password-form .form-input {
  width: 100%;
}

.submit-btn {
  margin-top: 20px;
  width: 100%;
  padding: 12px 40px;
  background: #3d8c40;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover {
  background: #2e7d32;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .main-wrapper {
    margin: 20px auto;
    padding: 0 15px;
  }

  .profile-layout {
    flex-direction: column;
  }

  .password-card {
    width: 100%;
  }

  .profile-card,
  .password-card {
    padding: 20px 15px;
  }

  .avatar-section {
    padding: 15px;
  }

  .profile-avatar {
    width: 60px;
    height: 60px;
  }

  .form-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .form-label {
    width: auto;
  }

  .form-input-wrapper {
    width: 100%;
    flex-wrap: wrap;
  }

  .form-input {
    width: 100%;
  }
}
</style>
